import {
  FlowOperationRequest,
  FlowOperationType,
  FlowRun,
  FlowVersion,
  FlowVersionState,
  Permission,
  PopulatedFlow,
  TriggerType,
  flowOperations,
  flowStructureUtil,
  isNil,
  StepLocationRelativeToParent,
  Action,
  isFlowStateTerminal,
  GenerateFlowRequest,
  WebsocketServerEvent,
  WebsocketClientEvent,
  AskAiBuilderResponse,
  isFlowControlStep,
  Step,
  AI_BUILDER_CLIENT_TIMEOUT_MS,
} from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import { useReactFlow } from '@xyflow/react';
import { nanoid } from 'nanoid';
import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from 'react';
import { usePrevious } from 'react-use';
import { Socket } from 'socket.io-client';
import { create, useStore } from 'zustand';

import { flowRunUtils } from '../../features/flow-runs/lib/flow-run-utils';
import { AskAiButtonOperations } from '../../features/pieces/lib/types';
import { useAuthorization } from '../../hooks/authorization-hooks';

import {
  copySelectedNodes,
  deleteSelectedNodes,
  getActionsInClipboard,
  pasteNodes,
  toggleSkipSelectedNodes,
} from './flow-canvas/bulk-actions';
import {
  CanvasShortcuts,
  CanvasShortcutsProps,
} from './flow-canvas/context-menu/canvas-context-menu';
import { STEP_CONTEXT_MENU_ATTRIBUTE } from './flow-canvas/utils/consts';
import { flowCanvasUtils } from './flow-canvas/utils/flow-canvas-utils';

import { showErrorToast } from '@/components/ui/use-toast';
import { flowRunsApi } from '@/features/flow-runs/lib/flow-runs-api';
import { flowsApi } from '@/features/flows/lib/flows-api';
import { PromiseQueue } from '@/lib/promise-queue';
import { sentry } from '@/lib/sentry';

const flowUpdatesQueue = new PromiseQueue();

const PENDING_SAVE_POLL_MS = 50;
// Bounded so a missed resolve can never freeze a caller (e.g. a component
// unmounting while its debounce or request is still pending)
const PENDING_SAVE_MAX_WAIT_MS = 5000;
// Writes that will reach the queue but are not enqueued yet: a debounced
// settings save, or an in-flight dynamic-property resolution. The key decides
// how repeats behave: a fixed key collapses them, which is what a debounce
// needs since it resolves once for every edit it swallowed.
const pendingFlowWrites = new Set<string>();

let pendingFlowWriteSequence = 0;
/**
 * Key for ONE in-flight write, whose register and resolve pair up 1:1. A fixed
 * key would be wrong there: two overlapping resolutions of the same property
 * share it, so the first to settle clears the flag while the second is still
 * running and waitForSavesToSettle returns over a write in flight.
 */
export const nextPendingFlowWriteKey = (source: string): string => {
  pendingFlowWriteSequence += 1;
  return `${source}#${pendingFlowWriteSequence}`;
};

export const BuilderStateContext = createContext<BuilderStore | null>(null);

export function useBuilderStateContext<T>(
  selector: (state: BuilderState) => T
): T {
  return useStore(useBuilderStore(), selector);
}

export function useBuilderStore(): BuilderStore {
  const store = useContext(BuilderStateContext);
  if (!store)
    throw new Error('Missing BuilderStateContext.Provider in the tree');
  return store;
}

export enum LeftSideBarType {
  RUNS = 'runs',
  VERSIONS = 'versions',
  RUN_DETAILS = 'run-details',
  AI_COPILOT = 'chat',
  AI_BUILDER = 'ai-builder',
  NONE = 'none',
}

export enum RightSideBarType {
  NONE = 'none',
  PIECE_SETTINGS = 'piece-settings',
}

type InsertMentionHandler = (propertyPath: string) => void;
export type BuilderState = {
  flow: PopulatedFlow;
  flowVersion: FlowVersion;
  readonly: boolean;
  sampleData: Record<string, unknown>;
  loopsIndexes: Record<string, number>;
  run: FlowRun | null;
  leftSidebar: LeftSideBarType;
  rightSidebar: RightSideBarType;
  selectedStep: string | null;
  canExitRun: boolean;
  activeDraggingStep: string | null;
  saving: boolean;
  /** change this value to trigger the step form to set its values from the step */
  refreshStepFormSettingsToggle: boolean;
  selectedBranchIndex: number | null;
  refreshSettings: () => void;
  setSelectedBranchIndex: (index: number | null) => void;
  exitRun: (userHasPermissionToEditFlow: boolean) => void;
  exitStepSettings: () => void;
  renameFlowClientSide: (newName: string) => void;
  moveToFolderClientSide: (folderId: string) => void;
  setRun: (
    run: FlowRun,
    flowVersion: FlowVersion,
    openRightSidebar?: boolean
  ) => void;
  setLeftSidebar: (leftSidebar: LeftSideBarType) => void;
  setRightSidebar: (rightSidebar: RightSideBarType) => void;
  applyOperation: (
    operation: FlowOperationRequest,
    onError: () => void
  ) => void;
  removeStepSelection: () => void;
  selectStepByName: (stepName: string) => void;
  startSaving: () => void;
  waitForSavesToSettle: () => Promise<void>;
  /** Signals a write that will reach the save queue but is not enqueued yet
   * (debounced panel edit, in-flight dynamic-property resolution) */
  registerPendingFlowWrite: (key: string) => void;
  resolvePendingFlowWrite: (key: string) => void;
  setActiveDraggingStep: (stepName: string | null) => void;
  setFlow: (flow: PopulatedFlow) => void;
  setSampleData: (stepName: string, payload: unknown) => void;
  exitPieceSelector: () => void;
  setVersion: (flowVersion: FlowVersion) => void;
  insertMention: InsertMentionHandler | null;
  setReadOnly: (readOnly: boolean) => void;
  setInsertMentionHandler: (handler: InsertMentionHandler | null) => void;
  setLoopIndex: (stepName: string, index: number) => void;
  operationListeners: Array<
    (flowVersion: FlowVersion, operation: FlowOperationRequest) => void
  >;
  addOperationListener: (
    listener: (
      flowVersion: FlowVersion,
      operation: FlowOperationRequest
    ) => void
  ) => void;
  removeOperationListener: (
    listener: (
      flowVersion: FlowVersion,
      operation: FlowOperationRequest
    ) => void
  ) => void;
  askAiButtonProps: AskAiButtonOperations | null;
  setAskAiButtonProps: (props: AskAiButtonOperations | null) => void;
  selectedNodes: string[];
  setSelectedNodes: (nodes: string[]) => void;
  panningMode: 'grab' | 'pan';
  setPanningMode: (mode: 'grab' | 'pan') => void;
  pieceSelectorStep: string | null;
  setPieceSelectorStep: (step: string | null) => void;
  askAiBuilderButtonProps: null;
  setAskAiBuilderButtonProps: () => void;
  applyAiFlowUpdate: (flowVersion: FlowVersion) => void;
  /**
   * Merge the AI turn's per-step state (validity + settings) and the resolved
   * dynamic properties from the server version, keeping the local structure
   * and layout. Settings are applied ONLY where the server differs from the
   * pre-turn baseline: an identical server step may be STALER than a panel
   * edit made while the turn was running, and overwriting it would then be
   * persisted by the panel's autosave.
   */
  applyAiStepState: (
    serverVersion: FlowVersion,
    baselineVersion: FlowVersion | null,
  ) => void;
  applyAiPartialFlowElements: (flowElements: {
    triggers?: FlowVersion['triggers'];
    actions?: FlowVersion['actions'];
    connections?: FlowVersion['connections'];
  }) => void;
  generateFlow: (
    params: GenerateFlowRequest,
    socket: Socket
  ) => Promise<AskAiBuilderResponse>;
  testTriggerFlag: boolean;
  triggerTest: () => void;
  resetTriggerTest: () => void;
  autoLayoutFlag: boolean;
  triggerAutoLayout: () => void;
  resetAutoLayout: () => void;
  /** Re-render the canvas from the (locally laid out) flow and refit the view */
  canvasRefitFlag: boolean;
  triggerCanvasRefit: () => void;
  resetCanvasRefit: () => void;
  aiHelpRequest: string | null;
  requestAiHelp: (stepName: string) => void;
  clearAiHelpRequest: () => void;
  connectionDialogOpen: boolean;
  setConnectionDialogOpen: (open: boolean) => void;
  /** Bumped on each connection created in the builder — the AI chat resumes its wizard on it */
  connectionCreatedNonce: number;
  notifyConnectionCreated: () => void;
  highlightKey: string | null;
  setHighlightKey: (key: string | null) => void;
  clearHighlightKey: () => void;
};
const DEFAULT_PANNING_MODE_KEY_IN_LOCAL_STORAGE = 'defaultPanningMode';
export type BuilderInitialState = Pick<
  BuilderState,
  'flow' | 'flowVersion' | 'readonly' | 'run' | 'canExitRun' | 'sampleData'
>;

export type BuilderStore = ReturnType<typeof createBuilderStore>;

/** Lays every step out from scratch — the AI creates them all at {0,0}. */
/** Positions are laid out locally, so they never count as a server change. */
const settingsWithoutPosition = (step: {
  settings: Record<string, unknown>;
}): Record<string, unknown> => {
  const { position: _position, ...rest } = step.settings;
  return rest;
};

const stepsByName = (flowVersion: FlowVersion): Map<string, Step> =>
  new Map(
    flowStructureUtil.getAllSteps(flowVersion).map((step) => [step.name, step]),
  );

/** Indexes both versions once, so the per-step merge carries no lookup cost */
const createStepMerger = (
  serverVersion: FlowVersion,
  baselineVersion: FlowVersion | null,
): ((step: Step) => Step) => {
  const serverStepByName = stepsByName(serverVersion);
  const baselineStepByName = baselineVersion
    ? stepsByName(baselineVersion)
    : new Map<string, Step>();
  return (step) => mergeServerStep(step, serverStepByName, baselineStepByName);
};

/**
 * The server's state for one step, applied ONLY where it differs from the
 * pre-turn baseline: an identical server step may be staler than a panel edit
 * made while the turn was running.
 */
const mergeServerStep = (
  step: Step,
  serverStepByName: Map<string, Step>,
  baselineStepByName: Map<string, Step>,
): Step => {
  const serverStep = serverStepByName.get(step.name);
  if (!serverStep) return step;
  const baselineStep = baselineStepByName.get(step.name);
  const turnChangedSettings =
    !baselineStep ||
    JSON.stringify(settingsWithoutPosition(serverStep)) !==
      JSON.stringify(settingsWithoutPosition(baselineStep));
  if (!turnChangedSettings) {
    return serverStep.valid === step.valid
      ? step
      : { ...step, valid: serverStep.valid };
  }
  return {
    ...step,
    valid: serverStep.valid,
    // Positions are laid out locally — the server keeps {0,0}
    settings: {
      ...serverStep.settings,
      position: step.settings.position,
    },
  };
};

/**
 * Same rule as the steps: the server's name only when the turn gave it (it
 * names the flow at the end of a finished build), and never over a rename the
 * user made while the turn was running.
 */
const mergeServerFlowName = (
  localName: string,
  serverVersion: FlowVersion,
  baselineVersion: FlowVersion | null,
): string => {
  if (!baselineVersion) return serverVersion.displayName;
  const turnRenamedFlow =
    serverVersion.displayName !== baselineVersion.displayName;
  const userRenamedDuringTurn = localName !== baselineVersion.displayName;
  return turnRenamedFlow && !userRenamedDuringTurn
    ? serverVersion.displayName
    : localName;
};

const withLayoutPositions = (flowVersion: FlowVersion): FlowVersion => {
  const positions = flowCanvasUtils.computeLayoutPositions(flowVersion);
  return flowStructureUtil.transferFlow(flowVersion, (step) => {
    const position = positions.get(step.name);
    if (!position) return step;
    return { ...step, settings: { ...step.settings, position } };
  });
};

/**
 * Resolves with the answer to one AI Builder turn. The socket is shared by
 * every turn and every tab, hence the requestId filter; the timeout only fires
 * when the server never answered at all, since it answers within its own budget.
 */
const waitForTurnResponse = (
  socket: Socket,
  requestId: string,
): Promise<AskAiBuilderResponse> =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      socket.off(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
      reject(new Error('Timeout waiting for generate flow response'));
    }, AI_BUILDER_CLIENT_TIMEOUT_MS);

    function onFinished(data: AskAiBuilderResponse) {
      if (data.requestId && data.requestId !== requestId) {
        return;
      }
      clearTimeout(timeout);
      socket.off(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
      resolve(data);
    }

    socket.on(WebsocketClientEvent.GENERATE_FLOW_FINISHED, onFinished);
  });

export const createBuilderStore = (initialState: BuilderInitialState) =>
  create<BuilderState>((set) => {
    const initiallySelectedStep = initialState.run?.steps
      ? flowRunUtils.findFailedStepInOutput(initialState.run.steps)
      : null;
    return {
      loopsIndexes: initialState.run?.steps
        ? flowRunUtils.findLoopsState(
            initialState.flowVersion,
            initialState.run,
            {}
          )
        : {},
      sampleData: initialState.sampleData,
      flow: initialState.flow,
      flowVersion: initialState.flowVersion,
      leftSidebar: initialState.run
        ? LeftSideBarType.RUN_DETAILS
        : LeftSideBarType.NONE,
      readonly: initialState.readonly,
      run: initialState.run,
      saving: false,
      selectedStep: initiallySelectedStep,
      selectedNodes: initiallySelectedStep ? [initiallySelectedStep] : [],
      canExitRun: initialState.canExitRun,
      activeDraggingStep: null,
      rightSidebar:
        initiallySelectedStep &&
        (initiallySelectedStep !== 'trigger' ||
          initialState.flowVersion.triggers[0].type !== TriggerType.EMPTY)
          ? RightSideBarType.PIECE_SETTINGS
          : RightSideBarType.NONE,
      refreshStepFormSettingsToggle: false,

      removeStepSelection: () =>
        set({
          selectedStep: null,
          rightSidebar: RightSideBarType.NONE,
          selectedBranchIndex: null,
        }),

      setActiveDraggingStep: (stepName: string | null) =>
        set({
          activeDraggingStep: stepName,
        }),
      setSelectedBranchIndex: (branchIndex: number | null) =>
        set({
          selectedBranchIndex: branchIndex,
        }),
      setReadOnly: (readonly: boolean) => set({ readonly }),
      renameFlowClientSide: (newName: string) => {
        set((state) => {
          return {
            flowVersion: {
              ...state.flowVersion,
              displayName: newName,
            },
          };
        });
      },
      selectStepByName: (selectedStep: string) => {
        set((state) => {
          if (selectedStep === state.selectedStep) {
            return state;
          }

          const step = flowStructureUtil.getStep(
            selectedStep,
            state.flowVersion
          );
          if (step && isFlowControlStep(step.settings.pieceName)) {
            return state;
          }

          const selectedNodes = isNil(selectedStep) ? [] : [selectedStep];

          const rightSidebar =
            selectedStep === 'trigger' &&
            state.flowVersion.triggers[0].type === TriggerType.EMPTY
              ? RightSideBarType.NONE
              : RightSideBarType.PIECE_SETTINGS;

          const leftSidebar =
            state.leftSidebar === LeftSideBarType.AI_BUILDER
              ? LeftSideBarType.AI_BUILDER
              : !isNil(state.run)
              ? LeftSideBarType.RUN_DETAILS
              : LeftSideBarType.NONE;
          return {
            selectedStep,
            rightSidebar,
            leftSidebar,
            selectedBranchIndex: null,
            askAiButtonProps: null,
            selectedNodes,
          };
        });
      },
      moveToFolderClientSide: (folderId: string) => {
        set((state) => {
          return {
            flow: {
              ...state.flow,
              folderId,
            },
          };
        });
      },
      setFlow: (flow: PopulatedFlow) => set({ flow, selectedStep: null }),
      setSampleData: (stepName: string, payload: unknown) =>
        set((state) => {
          return {
            sampleData: {
              ...state.sampleData,
              [stepName]: payload,
            },
          };
        }),
      exitRun: (userHasPermissionToEditFlow: boolean) =>
        set({
          run: null,
          readonly: !userHasPermissionToEditFlow,
          loopsIndexes: {},
          leftSidebar: LeftSideBarType.NONE,
          rightSidebar: RightSideBarType.NONE,
          selectedBranchIndex: null,
        }),
      exitStepSettings: () =>
        set((state) => ({
          rightSidebar: RightSideBarType.NONE,
          leftSidebar:
            state.leftSidebar === LeftSideBarType.AI_COPILOT
              ? LeftSideBarType.NONE
              : state.leftSidebar,
          selectedStep: null,
          selectedBranchIndex: null,
          askAiButtonProps: null,
        })),
      exitPieceSelector: () =>
        set({
          rightSidebar: RightSideBarType.NONE,
          selectedBranchIndex: null,
        }),
      setRightSidebar: (rightSidebar: RightSideBarType) =>
        set({ rightSidebar }),
      setLeftSidebar: (leftSidebar: LeftSideBarType) =>
        set({ leftSidebar, askAiButtonProps: null }),
      setRun: async (
        run: FlowRun,
        flowVersion: FlowVersion,
        openRightSidebar = true
      ) =>
        set((state) => {
          const failedStep = run.steps
            ? flowRunUtils.findFailedStepInOutput(run.steps)
            : null;
          const defaultStep = failedStep ?? state.selectedStep ?? 'trigger';
          const isInitialRunLoad = state.run?.id !== run.id;

          return {
            loopsIndexes: flowRunUtils.findLoopsState(
              flowVersion,
              run,
              state.loopsIndexes
            ),
            run,
            flowVersion,
            leftSidebar: isInitialRunLoad
              ? LeftSideBarType.RUN_DETAILS
              : state.leftSidebar,
            rightSidebar: openRightSidebar
              ? RightSideBarType.PIECE_SETTINGS
              : RightSideBarType.NONE,
            selectedStep: openRightSidebar ? defaultStep : null,
            readonly: true,
          };
        }),
      startSaving: () => set({ saving: true }),
      waitForSavesToSettle: async () => {
        // Some writes are not enqueued yet when we are called: a debounced panel
        // edit, or a dynamic-property resolution still waiting on its HTTP call.
        // Wait for those, then for the queue itself to drain.
        const deadline = Date.now() + PENDING_SAVE_MAX_WAIT_MS;
        while (pendingFlowWrites.size > 0 && Date.now() < deadline) {
          await new Promise((resolve) =>
            setTimeout(resolve, PENDING_SAVE_POLL_MS)
          );
        }
        await flowUpdatesQueue.waitForIdle();
      },
      registerPendingFlowWrite: (key: string) => {
        pendingFlowWrites.add(key);
      },
      resolvePendingFlowWrite: (key: string) => {
        pendingFlowWrites.delete(key);
      },
      setLoopIndex: (stepName: string, index: number) => {
        set((state) => {
          return {
            loopsIndexes: {
              ...state.loopsIndexes,
              [stepName]: index,
            },
          };
        });
      },
      applyOperation: (operation: FlowOperationRequest, onError: () => void) =>
        set((state) => {
          if (state.readonly) {
            console.warn('Cannot apply operation while readonly');
            return state;
          }
          const newFlowVersion = flowOperations.apply(
            state.flowVersion,
            operation
          );

          state.operationListeners.forEach((listener) => {
            listener(state.flowVersion, operation);
          });

          const updateRequest = async () => {
            set({ saving: true });
            try {
              const updatedFlowVersion = await flowsApi.update(
                state.flow.id,
                operation
              );
              set((state) => {
                return {
                  flowVersion: {
                    ...state.flowVersion,
                    id: updatedFlowVersion.version.id,
                    state: updatedFlowVersion.version.state,
                  },
                  saving: flowUpdatesQueue.size() !== 0,
                };
              });
            } catch (error) {
              sentry.captureException(
                error,
                'Flow update failed - data loss risk',
                {
                  tags: {
                    flowId: state.flow.id,
                    operationType: operation.type,
                  },
                  extra: {
                    flowId: state.flow.id,
                    flowName: state.flow.version.displayName,
                    operation: operation,
                    queueSize: flowUpdatesQueue.size(),
                  },
                }
              );
              // The queue is halted on purpose (a dropped operation would
              // desync the flow), but the UI must stop claiming it is saving:
              // every later edit is discarded until the page is reloaded.
              flowUpdatesQueue.halt();
              set({ saving: false });
              onError();
            }
          };
          flowUpdatesQueue.add(updateRequest);
          return { flowVersion: newFlowVersion };
        }),
      setVersion: (flowVersion: FlowVersion) => {
        set((state) => ({
          flowVersion,
          run: null,
          selectedStep: null,
          readonly:
            state.flow.publishedVersionId !== flowVersion.id &&
            flowVersion.state === FlowVersionState.LOCKED,
          leftSidebar: LeftSideBarType.NONE,
          rightSidebar: RightSideBarType.NONE,
          selectedBranchIndex: null,
        }));
      },
      insertMention: null,
      setInsertMentionHandler: (insertMention: InsertMentionHandler | null) => {
        set({ insertMention });
      },
      refreshSettings: () =>
        set((state) => ({
          refreshStepFormSettingsToggle: !state.refreshStepFormSettingsToggle,
        })),

      selectedBranchIndex: null,
      operationListeners: [],
      addOperationListener: (
        listener: (
          flowVersion: FlowVersion,
          operation: FlowOperationRequest
        ) => void
      ) =>
        set((state) => ({
          operationListeners: [...state.operationListeners, listener],
        })),
      removeOperationListener: (
        listener: (
          flowVersion: FlowVersion,
          operation: FlowOperationRequest
        ) => void
      ) =>
        set((state) => ({
          operationListeners: state.operationListeners.filter(
            (l) => l !== listener
          ),
        })),
      askAiButtonProps: null,
      setAskAiButtonProps: (props) => {
        return set((state) => {
          let leftSidebar = state.leftSidebar;
          if (props) {
            leftSidebar = LeftSideBarType.AI_COPILOT;
          } else if (state.leftSidebar === LeftSideBarType.AI_COPILOT) {
            leftSidebar = LeftSideBarType.NONE;
          }

          let rightSidebar = state.rightSidebar;
          if (props && props.type === FlowOperationType.UPDATE_ACTION) {
            rightSidebar = RightSideBarType.PIECE_SETTINGS;
          } else if (props) {
            rightSidebar = RightSideBarType.NONE;
          }

          let selectedStep = state.selectedStep;
          if (props && props.type === FlowOperationType.UPDATE_ACTION) {
            selectedStep = props.stepName;
          } else if (props) {
            selectedStep = null;
          }

          return {
            askAiButtonProps: props,
            leftSidebar,
            rightSidebar,
            selectedStep,
          };
        });
      },
      setSelectedNodes: (nodes) => {
        return set(() => ({
          selectedNodes: nodes,
        }));
      },
      panningMode: getPanningModeFromLocalStorage(),
      setPanningMode: (mode: 'grab' | 'pan') => {
        localStorage.setItem(DEFAULT_PANNING_MODE_KEY_IN_LOCAL_STORAGE, mode);
        return set(() => ({
          panningMode: mode,
        }));
      },
      pieceSelectorStep: null,
      setPieceSelectorStep: (step: string | null) => {
        return set((state) => {
          return {
            pieceSelectorStep: step,
            selectedStep: step ? step : state.selectedStep,
            rightSidebar:
              (step && step !== 'trigger') ||
              state.flowVersion.triggers[0].type !== TriggerType.EMPTY
                ? RightSideBarType.PIECE_SETTINGS
                : state.rightSidebar,
          };
        });
      },
      askAiBuilderButtonProps: null,
      setAskAiBuilderButtonProps: () => {
        return set((state) => {
          let leftSidebar = state.leftSidebar;
          leftSidebar = LeftSideBarType.AI_BUILDER;
          if (state.leftSidebar === LeftSideBarType.AI_BUILDER) {
            leftSidebar = LeftSideBarType.NONE;
          }

          return {
            leftSidebar,
          };
        });
      },
      applyAiFlowUpdate: (flowVersion: FlowVersion) => {
        set((state) => ({
          flowVersion,
          refreshStepFormSettingsToggle: !state.refreshStepFormSettingsToggle,
        }));
      },
      applyAiStepState: (
        serverVersion: FlowVersion,
        baselineVersion: FlowVersion | null,
      ) => {
        const mergeStep = createStepMerger(serverVersion, baselineVersion);
        set((state) => {
          const withServerState = flowStructureUtil.transferFlow(
            state.flowVersion,
            mergeStep,
          );
          return {
            flowVersion: {
              ...withServerState,
              displayName: mergeServerFlowName(
                state.flowVersion.displayName,
                serverVersion,
                baselineVersion,
              ),
              valid: serverVersion.valid,
              // Additive: a refresh resolved locally during the turn survives
              dynamicPropertiesValues: {
                ...state.flowVersion.dynamicPropertiesValues,
                ...serverVersion.dynamicPropertiesValues,
              },
            },
            // The open settings panel only re-reads its step when this toggle
            // flips. Without it the form keeps the values from before the turn,
            // and the next keystroke saves them back over the AI's work.
            refreshStepFormSettingsToggle: !state.refreshStepFormSettingsToggle,
          };
        });
      },
      applyAiPartialFlowElements: (flowElements) => {
        // Local canvas preview only — the agent persists server-side at the end
        // of its turn; writing this partial state back through the API would
        // race its own writes. Positions are laid out locally for the same
        // reason: the agent creates every step at {0,0}, and the persisting
        // layout (DRAG_STEP) would target steps the server does not know yet.
        set((state) => ({
          flowVersion: withLayoutPositions({
            ...state.flowVersion,
            triggers: flowElements.triggers ?? state.flowVersion.triggers,
            actions: flowElements.actions ?? state.flowVersion.actions,
            connections:
              flowElements.connections ?? state.flowVersion.connections,
          }),
        }));
      },
      generateFlow: async (params: GenerateFlowRequest, socket: Socket) => {
        const requestId = nanoid();
        socket.emit(WebsocketServerEvent.GENERATE_FLOW, {
          ...params,
          requestId,
        });

        try {
          return await waitForTurnResponse(socket, requestId);
        } catch (error) {
          sentry.captureException(error, 'AI flow generation failed', {
            tags: {
              feature: 'ai-builder',
              operation: 'generateFlow',
            },
            extra: {
              prompt: params.prompt,
            },
          });
          throw error;
        }
      },
      testTriggerFlag: false,
      triggerTest: () => set({ testTriggerFlag: true }),
      resetTriggerTest: () => set({ testTriggerFlag: false }),
      autoLayoutFlag: false,
      triggerAutoLayout: () => set({ autoLayoutFlag: true }),
      resetAutoLayout: () => set({ autoLayoutFlag: false }),
      canvasRefitFlag: false,
      triggerCanvasRefit: () => set({ canvasRefitFlag: true }),
      resetCanvasRefit: () => set({ canvasRefitFlag: false }),
      aiHelpRequest: null,
      requestAiHelp: (stepName: string) =>
        set({
          aiHelpRequest: stepName,
          leftSidebar: LeftSideBarType.AI_BUILDER,
        }),
      clearAiHelpRequest: () => set({ aiHelpRequest: null }),
      connectionDialogOpen: false,
      setConnectionDialogOpen: (open) => set({ connectionDialogOpen: open }),
      connectionCreatedNonce: 0,
      notifyConnectionCreated: () =>
        set((state) => ({
          connectionCreatedNonce: state.connectionCreatedNonce + 1,
        })),
      highlightKey: null,
      setHighlightKey: (key) => set({ highlightKey: key }),
      clearHighlightKey: () => set({ highlightKey: null }),
    };
  });

export function getPanningModeFromLocalStorage(): 'grab' | 'pan' {
  return localStorage.getItem(DEFAULT_PANNING_MODE_KEY_IN_LOCAL_STORAGE) ===
    'grab'
    ? 'grab'
    : 'pan';
}

const shortcutHandler = (
  event: KeyboardEvent,
  handlers: Record<keyof CanvasShortcutsProps, () => void>
) => {
  const shortcutActivated = Object.entries(CanvasShortcuts).find(
    ([_, shortcut]) =>
      shortcut.shortcutKey?.toLowerCase() === event.key.toLowerCase() &&
      !!(
        shortcut.withCtrl === event.ctrlKey ||
        shortcut.withCtrl === event.metaKey
      ) &&
      !!shortcut.withShift === event.shiftKey
  );
  if (shortcutActivated) {
    if (
      isNil(shortcutActivated[1].shouldNotPreventDefault) ||
      !shortcutActivated[1].shouldNotPreventDefault
    ) {
      event.preventDefault();
    }
    event.stopPropagation();
    handlers[shortcutActivated[0] as keyof CanvasShortcutsProps]();
  }
};

export const NODE_SELECTION_RECT_CLASS_NAME = 'react-flow__nodesselection-rect';
export const doesSelectionRectangleExist = () => {
  return document.querySelector(`.${NODE_SELECTION_RECT_CLASS_NAME}`) !== null;
};
export const useHandleKeyPressOnCanvas = () => {
  const [
    selectedNodes,
    flowVersion,
    selectedStep,
    exitStepSettings,
    applyOperation,
    readonly,
  ] = useBuilderStateContext((state) => [
    state.selectedNodes,
    state.flowVersion,
    state.selectedStep,
    state.exitStepSettings,
    state.applyOperation,
    state.readonly,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        (e.target === document.body ||
          e.target.classList.contains('react-flow__nodesselection-rect') ||
          e.target.closest(`[data-${STEP_CONTEXT_MENU_ATTRIBUTE}]`)) &&
        !readonly
      ) {
        const selectedNodesWithoutTrigger = selectedNodes.filter(
          (node) => node !== flowVersion.triggers[0].name
        );
        shortcutHandler(e, {
          Copy: () => {
            if (
              selectedNodesWithoutTrigger.length > 0 &&
              document.getSelection()?.toString() === ''
            ) {
              copySelectedNodes({
                selectedNodes: selectedNodesWithoutTrigger,
                flowVersion,
              });
            }
          },
          Delete: () => {
            if (selectedNodes.length > 0) {
              deleteSelectedNodes({
                exitStepSettings,
                selectedStep,
                selectedNodes,
                applyOperation,
              });
            }
          },
          Skip: () => {
            if (selectedNodesWithoutTrigger.length > 0) {
              toggleSkipSelectedNodes({
                selectedNodes: selectedNodesWithoutTrigger,
                flowVersion,
                applyOperation,
              });
            }
          },
          Paste: () => {
            getActionsInClipboard().then((actions) => {
              if (actions.length > 0) {
                const lastStep = [
                  flowVersion.triggers[0],
                  ...flowStructureUtil.getAllNextActionsWithoutChildren(
                    flowVersion.triggers[0],
                    flowVersion
                  ),
                ].at(-1)!.name;
                const lastSelectedNode =
                  selectedNodes.length === 1 ? selectedNodes[0] : null;
                pasteNodes(
                  actions,
                  flowVersion,
                  {
                    parentStepName: lastSelectedNode ?? lastStep,
                    stepLocationRelativeToParent:
                      StepLocationRelativeToParent.AFTER,
                  },
                  applyOperation
                );
              }
            });
          },
        });
      }
    },
    [
      selectedNodes,
      flowVersion,
      applyOperation,
      selectedStep,
      exitStepSettings,
      readonly,
    ]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};

export const useSwitchToDraft = () => {
  const [flowVersion, setVersion, exitRun, run] = useBuilderStateContext(
    (state) => [state.flowVersion, state.setVersion, state.exitRun, state.run]
  );
  const { checkAccess } = useAuthorization();
  const userHasPermissionToEditFlow = checkAccess(Permission.WRITE_FLOW);
  const { mutate: switchToDraft, isPending: isSwitchingToDraftPending } =
    useMutation({
      mutationFn: async () => {
        if (run && !run.finishTime) {
          await flowRunsApi.cancel(run.id);
        }
        const flow = await flowsApi.get(flowVersion.flowId);
        return flow;
      },
      onSuccess: (flow) => {
        setVersion(flow.version);
        exitRun(userHasPermissionToEditFlow);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    });
  return {
    switchToDraft,
    isSwitchingToDraftPending,
  };
};

export const usePasteActionsInClipboard = () => {
  const [actionsToPaste, setActionsToPaste] = useState<Action[]>([]);
  const fetchClipboardOperations = async () => {
    if (document.hasFocus()) {
      const fetchedActionsFromClipboard = await getActionsInClipboard();
      if (fetchedActionsFromClipboard.length > 0) {
        setActionsToPaste(fetchedActionsFromClipboard);
      } else {
        setActionsToPaste([]);
      }
    }
  };
  return { actionsToPaste, fetchClipboardOperations };
};

export const useFocusedFailedStep = () => {
  const currentRun = useBuilderStateContext((state) => state.run);
  const previousRun = usePrevious(currentRun);
  const { fitView } = useReactFlow();
  if (
    (currentRun &&
      previousRun?.id !== currentRun.id &&
      isFlowStateTerminal(currentRun.status)) ||
    (currentRun &&
      previousRun &&
      !isFlowStateTerminal(previousRun.status) &&
      isFlowStateTerminal(currentRun.status))
  ) {
    const failedStep = currentRun.steps
      ? flowRunUtils.findFailedStepInOutput(currentRun.steps)
      : null;
    if (failedStep) {
      setTimeout(() => {
        fitView(flowCanvasUtils.createFocusStepInGraphParams(failedStep));
      });
    }
  }
};
